import { OrderByDirection } from '@google-cloud/firestore';
import { boolean, mixed, object, string } from 'yup';
import type { SchemaOf } from 'yup';

import { PageFirestore, PageGetList } from '@typings/page';
import { getIsPositionDefined } from '@utilities/block';
import { slugRegex } from '@utilities/slug';

import { dateSchema, keySchema, positionSchema } from './document';
import { localeSchema } from './locale';

export const pageNameSchema = string();
export const pageSlugSchema = string().test(
  'slugify',
  'Slugs must be lowercased with dashes and no spaces',
  (value) => {
    if (!value) return true;

    return slugRegex.test(value);
  }
);

// API schema
export const pagePostSchema: SchemaOf<PageFirestore> = object({
  brandguideKey: keySchema.required(),
  locale: localeSchema.required(),
  name: pageNameSchema.required(),
  slug: pageSlugSchema.required(),
  position: positionSchema.required(),
  createdAt: dateSchema.required(),
  updatedAt: dateSchema.required(),
  deletedAt: dateSchema.nullable()
}).noUnknown();

export const pagePatchSchema: SchemaOf<Partial<PageFirestore>> = object({
  brandguideKey: keySchema.optional(),
  locale: localeSchema.optional(),
  name: pageNameSchema.optional(),
  slug: pageSlugSchema.optional(),
  position: positionSchema.optional(),
  createdAt: dateSchema.optional(),
  updatedAt: dateSchema.required(),
  deletedAt: dateSchema.optional()
})
  .test(
    'at-least-one-property',
    'at least one of locale, name, slug, or position is required',
    (value) => {
      const { locale, name, slug, position } = value;
      return getIsPositionDefined(position) || !!locale || !!name || !!slug;
    }
  )
  .noUnknown();

export const pageGetListSchema: SchemaOf<PageGetList> = object({
  brandguideKey: keySchema.optional(),
  includeBlocks: boolean().optional(),
  locale: localeSchema.optional(),
  slug: pageSlugSchema.optional(),
  sort: mixed<OrderByDirection>().oneOf(['asc', 'desc']).optional()
}).noUnknown();
